import type { Option } from '../types/olo-api/Option';
import type { OptionGroup } from '../types/olo-api/OptionGroup';

/** Per-store (or merged) visibility rules — see docs/menu-visibility-by-location.md */
export type MenuVisibilityStoreConfig = {
  hiddenChainProductIds?: number[];
  hiddenChainOptionsByProduct?: Record<string, number[]>;
  hiddenChainOptionsByModifier?: Record<string, number[]>;
  hiddenChainOptionsByParent?: Record<string, number[]>;
};

export type MenuVisibilityRoot = Record<string, MenuVisibilityStoreConfig>;

function mapLookup(
  map: Record<string, number[]> | undefined,
  id: number,
): number[] | undefined {
  if (!map) return undefined;
  return map[String(id)];
}


function uniqueNums(a: number[]): number[] {
  return Array.from(new Set(a));
}

function mergeIdMap(
  base?: Record<string, number[]>,
  over?: Record<string, number[]>,
): Record<string, number[]> | undefined {
  if (!base && !over) return undefined;
  const out: Record<string, number[]> = { ...(base ?? {}) };
  if (over) {
    for (const k of Object.keys(over)) {
      out[k] = over[k];
    }
  }
  return Object.keys(out).length ? out : undefined;
}

export function mergeMenuVisibilityStoreConfig(
  base: MenuVisibilityStoreConfig,
  override: MenuVisibilityStoreConfig,
): MenuVisibilityStoreConfig {
  const hiddenChainProductIds = uniqueNums([
    ...(base.hiddenChainProductIds ?? []),
    ...(override.hiddenChainProductIds ?? []),
  ]);

  const out: MenuVisibilityStoreConfig = {
    hiddenChainProductIds: hiddenChainProductIds.length
      ? hiddenChainProductIds
      : undefined,
    hiddenChainOptionsByProduct: mergeIdMap(
      base.hiddenChainOptionsByProduct,
      override.hiddenChainOptionsByProduct,
    ),
    hiddenChainOptionsByModifier: mergeIdMap(
      base.hiddenChainOptionsByModifier,
      override.hiddenChainOptionsByModifier,
    ),
    hiddenChainOptionsByParent: mergeIdMap(
      base.hiddenChainOptionsByParent,
      override.hiddenChainOptionsByParent,
    ),
  };

  if (!out.hiddenChainProductIds?.length) delete out.hiddenChainProductIds;
  return out;
}

/** Reads `menu_visibility_config` or legacy `hide_drinks_location_wise` from Remote Config JSON. */
export function extractMenuVisibilityRoot(
  remoteConfigData: unknown,
): MenuVisibilityRoot | null {
  if (!remoteConfigData || typeof remoteConfigData !== 'object') return null;
  const d = remoteConfigData as Record<string, unknown>;

  if (d.menu_visibility_config && typeof d.menu_visibility_config === 'object') {
    return d.menu_visibility_config as MenuVisibilityRoot;
  }
  if (
    d.hide_drinks_location_wise &&
    typeof d.hide_drinks_location_wise === 'object'
  ) {
    return d.hide_drinks_location_wise as MenuVisibilityRoot;
  }
  return null;
}

/** Merges `all` / `*` with the current store `extref`. Store fields overlay defaults. */
/** Build saga/service filter context for `/products/:id/modifiers` (chain product id from menu JSON). */
export function getModifiersFilterContext(
  remoteConfigData: unknown,
  extref: string,
  chainProductId: number | undefined,
): {
  chainProductId: number;
  visibility: MenuVisibilityStoreConfig | null;
} | undefined {
  if (chainProductId === undefined || chainProductId === null) {
    return undefined;
  }
  return {
    chainProductId,
    visibility: getMenuVisibilityForStore(
      extractMenuVisibilityRoot(remoteConfigData),
      extref,
    ),
  };
}

export function getMenuVisibilityForStore(
  root: MenuVisibilityRoot | null,
  extref: string,
): MenuVisibilityStoreConfig | null {
  if (!root || extref === '') return null;
  const rawAll = root.all ?? root['*'];
  const all = rawAll ? { ...rawAll } : null;
  const storeKey = extref in root ? extref : String(Number(extref));
  const storeCfg = root[extref] ?? root[storeKey];

  if (!all && !storeCfg) return null;
  if (!all) return { ...storeCfg! };
  if (!storeCfg) return mergeMenuVisibilityStoreConfig({}, all);
  return mergeMenuVisibilityStoreConfig(all, storeCfg);
}

export function filterProductsByMenuVisibility<
  T extends { chainproductid?: number },
>(products: T[], visibility: MenuVisibilityStoreConfig | null): T[] {
  if (!visibility?.hiddenChainProductIds?.length) return products;
  const hide = new Set(visibility.hiddenChainProductIds);
  return products.filter((p) => p.chainproductid == null || !hide.has(p.chainproductid));
}

function hiddenSetForOptionsInGroup(
  visibility: MenuVisibilityStoreConfig,
  chainProductId: number,
  group: OptionGroup,
  parentChainOptionId?: number,
): Set<number> {
  const s = new Set<number>();
  const add = (arr?: number[]) => arr?.forEach((n) => s.add(n));
  add(mapLookup(visibility.hiddenChainOptionsByModifier, group.chainmodifierid));
  add(mapLookup(visibility.hiddenChainOptionsByProduct, chainProductId));
  if (parentChainOptionId != null) {
    add(mapLookup(visibility.hiddenChainOptionsByParent, parentChainOptionId));
  }
  return s;
}

function filterOptionNode(
  option: Option,
  chainProductId: number,
  visibility: MenuVisibilityStoreConfig,
): Option {
  const parentId = option.chainoptionid;
  const nextModifiers = filterApiOptionGroups(
    option.modifiers,
    chainProductId,
    visibility,
    parentId,
  );
  const nextOptions = filterApiOptionGroups(
    option.options,
    chainProductId,
    visibility,
    parentId,
  );
  return {
    ...option,
    modifiers: nextModifiers ?? [],
    options: nextOptions ?? [],
  };
}

/** Filters raw Olo option groups before `getNewMenuOptionGroups` / `convertMetaDataToOptions`. */
export function filterApiOptionGroups(
  groups: OptionGroup[] | undefined,
  chainProductId: number,
  visibility: MenuVisibilityStoreConfig | null,
  parentChainOptionId?: number,
): OptionGroup[] | undefined {
  if (!groups?.length || !visibility) return groups;

  return groups.map((group) => {
    const hide = hiddenSetForOptionsInGroup(
      visibility,
      chainProductId,
      group,
      parentChainOptionId,
    );
    let options = group.options.filter((o) => !hide.has(o.chainoptionid));
    options = options.map((o) =>
      filterOptionNode(o, chainProductId, visibility),
    );

    return {
      ...group,
      options,
    };
  });
}
